import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, useFormikContext } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import DeleteIcon from '@mui/icons-material/Delete';
import SignaturePad from './SignaturePad';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import {
  Divider,
  Alert,
  AlertTitle,
  FormControl,
  FormLabel,
  CircularProgress,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Chip, Button, Box, Select, MenuItem, InputLabel
} from '@mui/material';

import generateHireAgreementCrud from './hireAgreement.crud';
import {
  validationHireAgreementSchema,
  INITIAL_VALUES,
  termsAndConditions,
} from './hireAgreementConst';

import {
  FormikTextField,
  FormikSelect,
  FormikTime,
  FormikPicker,
} from '../formik-material';

// Shared styles for reuse
const sharedStyles = {
  // Input field styles
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fafafa',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: 'white',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      },
      '&.Mui-focused': {
        backgroundColor: 'white',
        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
        transform: 'translateY(-2px)',
      },
    },
    '& .MuiInputLabel-root': {
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
      transition: 'all 0.3s ease',
    },
  },

  // Button styles
  button: {
    primary: {
      backgroundColor: '#3b82f6',
      color: 'white',
      '&:hover': {
        backgroundColor: '#2563eb',
      },
    },
    outlined: {
      color: '#3b82f6',
      borderColor: '#3b82f6',
      '&:hover': {
        borderColor: '#2563eb',
        backgroundColor: '#eff6ff',
      },
    },
  },

  // Chip styles
  chip: {
    backgroundColor: '#f8fafc',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: '#e0f2fe',
      borderColor: '#0ea5e9',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
    '& .MuiChip-deleteIcon': {
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#ef4444',
      },
    },
  },

  // Section title styles
  sectionTitle: {
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },

  // Radio and Checkbox styles
  radioCheckbox: {
    color: '#3b82f6',
    '&.Mui-checked': {
      color: '#3b82f6',
    },
  },

  // Form control label styles
  formControlLabel: {
    margin: '8px 0',
    padding: '16px',
    backgroundColor: '#f9fafb',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      borderColor: '#3b82f6',
      backgroundColor: '#eff6ff',
    },
  },
};

// Ensure these are defined at the top-level, after imports
const paymentOptionDisplay = {
  upfront: 'Pay Upfront',
  monthly: 'Monthly Invoice',
};
const invoiceContactOptionDisplay = {
  same_as_contact: 'Same as Contact Person',
  same_as_present: 'Same as Person Present at Booking',
  different_contact: 'Different Contact',
};

function capitalizeWords(str) {
  if (typeof str === 'object' && str !== null) {
    return '';
  }
  return String(str || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Helper function to check if signature is valid (not empty)
function isValidSignature(signature) {
  if (!signature || signature === '') {
    return false;
  }
  // If it's a data URL, check if it's not just an empty canvas
  if (signature.startsWith('data:image/')) {
    // A completely empty canvas data URL would be very short
    // We can check if it's longer than a minimal empty canvas
    return signature.length > 100; // Empty canvas data URLs are typically around 22-50 characters
  }
  return true;
}

// Helper function to format dates
function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}



async function fetchLocationAddressByName(locationName, crud) {
  try {
    const response = await crud.getLocationAddress(locationName);
    return response.data?.address || null;
  } catch (error) {
    console.log('Error in fetchLocationAddressByName:', error);
    return null;
  }
}

function MainStep({ 
  capchaKey, 
  captchaRef, 
  isReadOnly, 
  facilities, 
  areas, 
  loadingFacilities, 
  loadingAreas, 
  facilitiesError, 
  areasError, 
  fetchAreas, 
  initialValues,
  locationAddress,
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const termsContainerRef = useRef(null);
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [selectedArea, setSelectedArea] = useState('');

  // Handle scroll detection for terms and conditions
  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
    setHasScrolledToBottom(isAtBottom);
  };
  const [selectedSubArea, setSelectedSubArea] = useState('');
  const signatureRef = useRef(null);

  
  const selectedAreas = initialValues?.area_names ? 
    (typeof initialValues.area_names === 'string' ? initialValues.area_names.split(',') : initialValues.area_names) :
    (Array.isArray(values.area_ids) 
      ? values.area_ids.map(id => areas.find(a => String(a.id) === String(id))?.name).filter(Boolean)
      : []);

  const selectedSubAreas = initialValues?.sub_area_names
    ? (typeof initialValues.sub_area_names === 'string'
        ? initialValues.sub_area_names.split(',').map(s => s.trim())
        : initialValues.sub_area_names)
    : [];

  const documentNames = React.useMemo(() => {
    if (initialValues?.documents_names) {
      try {
    
        const decodedString = decodeURIComponent(initialValues.documents_names);
  
        return decodedString.split(',').map(doc => {
          const [type, status] = doc.split(':').map(s => s.trim());
          return { 
            type: type || 'Unknown', 
            status: status || 'Not Uploaded'
          };
        }).filter(doc => doc.type !== 'Unknown'); 
      } catch (error) {
        console.error('Error parsing document names:', error);
        return [];
      }
    }
    return [];
  }, [initialValues?.documents_names]);

  return (
    <section className="w-full mb-4 sm:mb-8 px-0 sm:px-2 md:px-4">

      <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
       Location
      </h2>

      {isReadOnly ? (
        <div className="space-y-8">
          <FormikTextField
            label="Facility"
            name="facility_id"
            value={ initialValues?.facility_name || ''}
            variant="outlined"
            className="my-4"
            fullWidth
            disabled={true}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Areas"
            name="area_ids"
            value={Array.isArray(selectedAreas) ? selectedAreas.join(', ') : ''}
            variant="outlined"
            className="my-8"
            fullWidth
            disabled={true}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Sub Areas"
            name="sub_area_ids"
            value={Array.isArray(selectedSubAreas) ? selectedSubAreas.join(', ') : ''}
            variant="outlined"
            className="my-8"
            fullWidth
            disabled={true}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
        </div>
      ) : (
        // Editable view
        <>
          {/* Facility selection */}
          <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
            <FormControl fullWidth>
              {loadingFacilities ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : facilitiesError ? (
                <Alert severity="error">{facilitiesError}</Alert>
              ) : (
                <FormikSelect
                  labelId="facility-select-label"
                  selectId="facility-select"
                  name="facility_id"
                  label="Facility"
                  options={facilities}
                  fullWidth
                  customHandleChange={(val) => {
                    const selectedFacility = facilities.find(f => f.id === val);
                    setFieldValue('facility_id', val);
                    setFieldValue('location_id', selectedFacility?.locationId || val);
                    setFieldValue('area_ids', []); 
                    setFieldValue('sub_area_ids', []); 
                    fetchAreas(val);
                  }}
                  sx={sharedStyles.inputField}
                />
              )}
            </FormControl>
          </div>

          {/* Area selection */}
          <div className="border-2 border-[#e5e7eb] p-2 sm:p-4 md:p-6 rounded-xl mb-4 sm:mb-6 transition-all duration-300 hover:border-[#3b82f6] hover:shadow-lg">
            <FormControl fullWidth>
              {loadingAreas ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : areasError ? (
                <Alert severity="error">{areasError}</Alert>
              ) : (
                <>
                  <InputLabel 
                    id="area-select-label"
                    sx={sharedStyles.sectionTitle}
                  >
                    Area
                  </InputLabel>
                  <Select
                    labelId="area-select-label"
                    id="area-select"
                    value={selectedArea}
                    label="Area"
                    onChange={(e) => {
                      const newArea = e.target.value;
                      if (newArea && !values.area_ids?.includes(newArea)) {
                        setFieldValue('area_ids', [...(values.area_ids || []), newArea]);
                        setFieldValue('sub_area_ids', []); // reset sub-areas on area change
                        // fetchSubAreas(newArea);
                      }
                      setSelectedArea('');
                    }}
                    fullWidth
                    sx={sharedStyles.inputField}
                  >
                    <MenuItem value="">
                      <em>Select value from the drop down</em>
                    </MenuItem>
                    {areas.map((area) => (
                      <MenuItem 
                        key={area.id} 
                        value={String(area.id)}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#eff6ff',
                          },
                        }}
                      >
                        {area.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedAreas.map((areaName, index) => (
                      <Chip
                        key={index}
                        label={areaName}
                        onDelete={() =>
                          setFieldValue(
                            'area_ids',
                            values.area_ids.filter((id) => {
                              const area = areas.find(a => a.name === areaName);
                              return String(id) !== String(area?.id);
                            })
                          )
                        }
                        deleteIcon={<DeleteIcon />}
                        sx={sharedStyles.chip}
                      />
                    ))}
                  </Box>
                </>
              )}
            </FormControl>
          </div>

          {/* Sub-area selection */}
          <div className="border-2 border-[#e5e7eb] p-2 sm:p-4 md:p-6 rounded-xl mb-4 sm:mb-6 transition-all duration-300 hover:border-[#3b82f6] hover:shadow-lg">
            <FormControl fullWidth>
                <>
                  <InputLabel 
                    id="sub-area-select-label"
                    sx={sharedStyles.sectionTitle}
                  >
                    Sub Area
                  </InputLabel>
                  <Select
                    labelId="sub-area-select-label"
                    id="sub-area-select"
                    value={selectedSubArea}
                    label="Sub Area"
                    onChange={(e) => {
                      const newSubArea = e.target.value;
                      if (newSubArea && !values.sub_area_ids?.includes(newSubArea)) {
                        setFieldValue('sub_area_ids', [...(values.sub_area_ids || []), newSubArea]);
                      }
                      setSelectedSubArea('');
                    }}
                    fullWidth
                    sx={sharedStyles.inputField}
                  >
                    <MenuItem value="">
                      <em>Select value from the drop down</em>
                    </MenuItem>
                    {/* {subAreas.map((subArea) => (
                      <MenuItem 
                        key={subArea.id} 
                        value={String(subArea.id)}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#eff6ff',
                          },
                        }}
                      >
                        {subArea.name}
                      </MenuItem>
                    ))} */}
                  </Select>
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedSubAreas.map((subAreaName, index) => (
                      {/* <Chip
                        key={index}
                        label={subAreaName}
                        onDelete={() =>
                          setFieldValue(
                            'sub_area_ids',
                            values.sub_area_ids.filter((id) => {
                              const subArea = subAreas.find(a => a.name === subAreaName);
                              return String(id) !== String(subArea?.id);
                            })
                          )
                        }
                        deleteIcon={<DeleteIcon />}
                        sx={sharedStyles.chip}
                      /> */}
                    ))}
                  </Box>
                </>
            </FormControl>
          </div>
        </>
      )}

      {/* Hirer Details section */}
      <div className="my-4 sm:my-8">
        <h3 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
          Hirer Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* 1) Hirer / Organisation Name */}
          <FormikTextField
            label="Hirer / Organisation Name *"
            name="organization"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />

          {/* 2) ABN */}
          <FormikTextField
            label="ABN"
            name="abn"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            placeholder="XX XXX XXX XXX"
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />

          <div className="col-span-2">
            <FormikTextField
              label="Address *"
              name="address"
              variant="outlined"
              fullWidth
              disabled={isReadOnly}
              multiline
              rows={3}
              sx={sharedStyles.inputField}
              InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
            />
          </div>

         
          <FormikTextField
            label="Contact Person – First Name *"
            name="contact_first_name"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Contact Person – Last Name *"
            name="contact_last_name"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />

          <FormikTextField
            label="Phone Number *"
            name="phone"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Email *"
            name="email"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />

          <FormikTextField
            label="Person Present at Booking – First Name *"
            name="present_first_name"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Person Present at Booking – Last Name *"
            name="present_last_name"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
        </div>
      </div>

      <div className="mb-4 sm:mb-8">
        <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
          Details for Invoicing and Payments
        </h2>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4 }}>
          <FormControl component="fieldset" className="mb-4" sx={{ flex: 1 }}>
            <FormLabel 
              component="legend"
              sx={sharedStyles.sectionTitle}
            >
              Invoice Contact *
            </FormLabel>
            <RadioGroup
              name="invoice_contact_option"
              value={values.invoice_contact_option}
              onChange={(e) =>
                setFieldValue('invoice_contact_option', e.currentTarget.value)
              }
            >
              <FormControlLabel
                value="same_as_contact"
                control={
                  <Radio 
                    disabled={isReadOnly}
                    sx={sharedStyles.radioCheckbox}
                  />
                }
                label="Same as Contact Person"
                sx={sharedStyles.formControlLabel}
              />
              <FormControlLabel
                value="same_as_present"
                control={
                  <Radio 
                    disabled={isReadOnly}
                    sx={sharedStyles.radioCheckbox}
                  />
                }
                label="Same as Person Present at Booking"
                sx={sharedStyles.formControlLabel}
              />
              <FormControlLabel
                value="different_contact"
                control={
                  <Radio 
                    disabled={isReadOnly}
                    sx={sharedStyles.radioCheckbox}
                  />
                }
                label="Different Contact"
                sx={sharedStyles.formControlLabel}
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" className="mb-4" sx={{ flex: 1 }}>
            <FormLabel 
              component="legend"
              sx={sharedStyles.sectionTitle}
            >
              Payment Options *
            </FormLabel>
            <RadioGroup
              name="payment_option"
              value={values.payment_option}
              onChange={(e) =>
                setFieldValue('payment_option', e.currentTarget.value)
              }
            >
              <FormControlLabel
                value="upfront"
                control={
                  <Radio 
                    disabled={isReadOnly}
                    sx={sharedStyles.radioCheckbox}
                  />
                }
                label="Pay Upfront"
                sx={sharedStyles.formControlLabel}
              />
              <FormControlLabel
                value="monthly"
                control={
                  <Radio 
                    disabled={isReadOnly}
                    sx={sharedStyles.radioCheckbox}
                  />
                }
                label="Monthly Invoice"
                sx={sharedStyles.formControlLabel}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {values.invoice_contact_option === 'different_contact' && (
          <div className="grid grid-cols-2 gap-4">
            <FormikTextField
              label="Invoice Contact – First Name *"
              name="invoice_first_name"
              variant="outlined"
              fullWidth
              sx={sharedStyles.inputField}
              InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
            />
            <FormikTextField
              label="Invoice Contact – Last Name *"
              name="invoice_last_name"
              variant="outlined"
              fullWidth
              sx={sharedStyles.inputField}
              InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
            />
            <FormikTextField
              label="Invoice Email *"
              name="invoice_email"
              variant="outlined"
              fullWidth
              sx={sharedStyles.inputField}
              InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
            />
          </div>
        )}

        <Typography 
          variant="body2" 
          color="textSecondary" 
          sx={{ 
            color: '#64748b',
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}
        >
          Note: Fee will be as per the Fees and Charges issued by the Local
          Government or BlueFit if no applicable rate.
        </Typography>
      </div>

      <div className="mb-4 sm:mb-8">
        <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
          Hire Details
        </h2>
        <div className="mb-8">
      
          <FormikTextField
            label="Hiring Purpose (Title of Event) *"
            name="hiring_purpose"
            variant="outlined"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Equipment Required"
            name="equipment_required"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <FormikTextField
            label="Attendance – Expected Spectators (if applicable)"
            name="spectators"
            variant="outlined"
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTextField
            label="Attendance – Total (Participants + Spectators) *"
            name="total_attendance"
            variant="outlined"
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
        </div>
        <FormControl component="fieldset" className="mb-4 w-full">
          <FormLabel 
            component="legend"
            sx={sharedStyles.sectionTitle}
          >
            Will you be charging people to attend your booking?
          </FormLabel>
          <RadioGroup
            row
            name="is_charging_attendance"
            value={values.is_charging_attendance}
            onChange={(e) =>
              setFieldValue('is_charging_attendance', e.currentTarget.value)
            }
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              value="1"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={{
                    color: '#3b82f6',
                    '&.Mui-checked': { color: '#3b82f6' },
                  }}
                />
              }
              label="Yes"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
            <FormControlLabel
              value="0"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={{
                    color: '#3b82f6',
                    '&.Mui-checked': { color: '#3b82f6' },
                  }}
                />
              }
              label="No"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
          </RadioGroup>
        </FormControl>

        <FormControl fullWidth className="mb-4">
          <FormLabel component="legend" sx={sharedStyles.sectionTitle} disabled={isReadOnly}>
            Documents
          </FormLabel>
          <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
            {documentNames.length > 0 ? (
              documentNames.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700 capitalize">{doc.type}:</span>
                    <span className={`text-sm ${doc.status === 'Uploaded' ? 'text-green-600' : 'text-gray-500'}`}>
                      {doc.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 italic">No documents uploaded</div>
            )}
          </div>
        </FormControl>

        
        
        <div className="mb-4 sm:mb-8">
          <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-4 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
            Hiring Period
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <FormikPicker
            label="Start Date *"
            name="start_date"
            dateTime={false}
            fullWidth
            className="w-full mb-4"
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikPicker
            label="End Date *"
            name="end_date"
            dateTime={false}
            fullWidth
            className="w-full"
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
              
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12 mb-4">
          <FormikTime
            label="Hiring Period (In Time) *"
            name="in_time"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            className="w-full mb-4"
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
          <FormikTime
            label="Hiring Period (Out Time) *"
            name="out_time"
            fullWidth
            disabled={isReadOnly}
            sx={sharedStyles.inputField}
            className="w-full"
            InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
          />
        </div>
        <FormControl component="fieldset" className="mb-4">
          <FormLabel 
            component="legend"
            disabled={isReadOnly}
            sx={sharedStyles.sectionTitle}
          >
            Hiring Day(s) of the Week
          </FormLabel>
          <FormGroup row>
            {[
              { id: 'monday', label: 'Monday' },
              { id: 'tuesday', label: 'Tuesday' },
              { id: 'wednesday', label: 'Wednesday' },
              { id: 'thursday', label: 'Thursday' },
              { id: 'friday', label: 'Friday' },
              { id: 'saturday', label: 'Saturday' },
              { id: 'sunday', label: 'Sunday' },
            ].map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    disabled={isReadOnly}
                    checked={values.hiring_days.includes(opt.id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const prev = values.hiring_days;
                      if (checked) {
                        setFieldValue('hiring_days', prev.concat(opt.id));
                      } else {
                        setFieldValue(
                          'hiring_days',
                          prev.filter((day) => day !== opt.id)
                        );
                      }
                    }}
                    name={opt.id}
                  />
                }
                label={opt.label}
                sx={{
                  margin: '4px',
                  padding: '4px',
                  backgroundColor: '#f8fafc',
                  border: '2px solid transparent',
                  borderRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  minWidth: '120px',
                  '&:hover': {
                    backgroundColor: '#e0f2fe',
                    borderColor: '#0ea5e9',
                  },
                }}
              />
            ))}
          </FormGroup>
        </FormControl>

        <FormControl component="fieldset" className="mb-4 w-full">
          <FormLabel 
            component="legend"
            sx={sharedStyles.sectionTitle}
            disabled={isReadOnly}
          >
            Will the booking continue throughout the school holidays?
          </FormLabel>
          <RadioGroup
            row
            name="continues_school_holidays"
            value={values.continues_school_holidays}
            onChange={(e) =>
              setFieldValue('continues_school_holidays', e.currentTarget.value)
            }
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              value="1"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={sharedStyles.radioCheckbox}
                />
              }
              label="Yes"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
            <FormControlLabel
                value="0"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={sharedStyles.radioCheckbox}
                />
              }
              label="No"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
          </RadioGroup>
        </FormControl>
        <FormControl component="fieldset">
          <FormLabel 
            component="legend"
            disabled={isReadOnly}
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
            }}
          >
            Are there any dates the booking will not be required throughout
            the year or booking period?
          </FormLabel>
          <RadioGroup
            row
            name="has_booking_exceptions"
            value={values.has_booking_exceptions}
            onChange={(e) => {
              setFieldValue('has_booking_exceptions', e.currentTarget.value);
              // Clear exception dates when "No" is selected
              if (e.currentTarget.value === '0') {
                setFieldValue('booking_exception_dates', []);
              }
            }}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              value="1"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={sharedStyles.radioCheckbox}
                />
              }
              label="Yes"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
            <FormControlLabel
              value="0"
              control={
                <Radio
                  disabled={isReadOnly}
                  sx={sharedStyles.radioCheckbox}
                />
              }
              label="No"
              sx={{
                flex: 1,
                margin: 0,
                padding: '8px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'none',
                '&:hover, &.Mui-focused': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#eff6ff',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.08)',
                },
              }}
            />
          </RadioGroup>
        </FormControl>
        {/* Display exception dates in read-only mode */}
        {values.has_booking_exceptions === '1' && values.booking_exception_dates && (
          (() => {
            // Handle different formats of booking_exception_dates
            let exceptionDates = values.booking_exception_dates;
            
            // If it's a string, try to parse it as JSON
            if (typeof exceptionDates === 'string') {
              try {
                exceptionDates = JSON.parse(exceptionDates);
              } catch (e) {
                console.warn('Failed to parse booking_exception_dates:', e);
                exceptionDates = [];
              }
            }
            
            // Ensure it's an array and has items
            if (!Array.isArray(exceptionDates) || exceptionDates.length === 0) {
              return null;
            }
            
                          return (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-md font-medium text-gray-900 mb-3">Exception Dates:</h3>
                  <div className="flex flex-wrap gap-2">
                    {exceptionDates.map((item, index) => (
                      <span 
                        key={index} 
                        className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-white text-gray-700 border border-gray-200 shadow-sm"
                      >
                        {typeof item === 'string' 
                          ? formatDate(item)
                          : item.type === 'single' 
                            ? formatDate(item.date)
                            : `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`
                        }
                      </span>
                    ))}
                  </div>
                </div>
              );
          })()
        )}
      </div>

      <div className="mb-4">
        <FormikTextField
          label="Additional Information"
          name="additional_notes"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          disabled={isReadOnly}
          className="w-full"
          sx={sharedStyles.inputField}
          InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
        />
      </div>

        <div className="mb-4 sm:mb-8 bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] rounded-2xl p-2 sm:p-4 md:p-8 border-2 border-[#e2e8f0] transition-all duration-300 hover:shadow-lg">
          <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
            Terms and Conditions
          </h2>
          <div 
            ref={termsContainerRef}
            onScroll={handleTermsScroll}
            className="max-h-[200px] sm:max-h-[300px] overflow-y-auto bg-white p-2 sm:p-4 md:p-6 rounded-lg text-sm leading-relaxed text-[#4b5563] shadow-inner"
          >
            <h3 className="font-semibold mb-4 text-[#1a202c]">CONDITIONS OF HIRE</h3>
            <ol className="list-decimal pl-6 space-y-4">
              {termsAndConditions.map((term, termIdx) => (
                <li key={termIdx} className="mb-6">
                  <span className="font-semibold">{term.title}</span>
                  <ol className="list-decimal pl-6 mt-2 space-y-2">
                    {term.clauses.map((clause, clauseIdx) => (
                      <li key={clauseIdx} className="mb-3">
                        <span>{clause.text.replace(/"Facility Name"/g, locationAddress || initialValues?.address || 'N/A')}</span>
                        {Array.isArray(clause.subClauses) &&
                          clause.subClauses.length > 0 && (
                            <ul className="list-[lower-alpha] pl-6 mt-1 space-y-1">
                              {clause.subClauses.map((subClause, subIdx) => (
                                <li key={subIdx} className="mb-1">
                                  {subClause.text.replace(/"Facility Name"/g, locationAddress || initialValues?.address || 'N/A')}
                                </li>
                              ))}
                            </ul>
                          )}
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
          </div>
          <FormControlLabel
            control={
              <Checkbox
                name="termsCheck"
                color="primary"
                checked={values.termsCheck}
                disabled={!hasScrolledToBottom}
                onChange={(e) => setFieldValue('termsCheck', e.target.checked)}
                sx={{
                  color: hasScrolledToBottom ? '#3b82f6' : '#9ca3af',
                  transition: 'all 0.3s ease',
                  '&.Mui-checked': {
                    color: '#3b82f6',
                    transform: 'scale(1.1)',
                  },
                  '&.Mui-disabled': {
                    color: '#9ca3af',
                  },
                }}
              />
            }
            label={
              <span style={{ 
                color: hasScrolledToBottom ? '#374151' : '#9ca3af',
                transition: 'color 0.3s ease'
              }}>
                {hasScrolledToBottom 
                  ? "I accept the Terms and Conditions" 
                  : "Please scroll to the bottom of the terms to accept"
                }
              </span>
            }
            sx={{
              marginTop: '20px',
              padding: '4px',
              opacity: hasScrolledToBottom ? 1 : 0.7,
              transition: 'opacity 0.3s ease',
            }}
          />
          {errors.termsCheck && touched.termsCheck && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.termsCheck}
            </Alert>
          )}
        </div>

        {/* Signer Information Section */}
        <div className="mb-4 sm:mb-8 bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] rounded-2xl p-2 sm:p-4 md:p-8 border-2 border-[#e2e8f0] transition-all duration-300 hover:shadow-lg">
          <h2 className="text-xl font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
            Signer Information
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <FormikTextField
              label="Name of Person Signing *"
              name="signed_name"
              variant="outlined"
              fullWidth
              placeholder="Enter your name"
              sx={sharedStyles.inputField}
              InputProps={{ sx: { fontSize: { xs: '14px', sm: '16px' } } }}
            />
          </div>

        </div>

        {/* Signature Section */}
        <div className="mb-4 sm:mb-8 bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] rounded-2xl p-2 sm:p-4 md:p-8 border-2 border-[#e2e8f0] transition-all duration-300 hover:shadow-lg">
          <h2 className="text-xl  font-bold text-[#1a202c] mb-4 sm:mb-8 relative pl-5 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-[30px] before:bg-[linear-gradient(135deg,_#3b82f6,_#8b5cf6)] before:rounded-[2px]">
            Digital Signature
          </h2>
          <SignaturePad 
            isReadOnly={isReadOnly} 
            onSignatureChange={(signature) => {
              setFieldValue('signature', signature);
              signatureRef.current = signature;
            }}
          />
          {errors.signature && touched.signature && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.signature}
            </Alert>
          )}
        </div> 
        <div className="mb-4 sm:mb-8 flex flex-col items-center">
          <ReCAPTCHA
            ref={captchaRef}
            sitekey={capchaKey}
            onChange={(token) => setFieldValue('recaptcha_token', token)}
            style={{ width: '100%', maxWidth: 340 }}
          />
          {errors.recaptcha_token && touched.recaptcha_token && (
            <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 340 }}>
              {errors.recaptcha_token}
            </Alert>
          )}
        </div>
      
    </section>
  );
}

MainStep.propTypes = {
  capchaKey: PropTypes.string,
  captchaRef: PropTypes.object,
  isReadOnly: PropTypes.bool,
  initialValues: PropTypes.object,
  locationAddress: PropTypes.string,
};


export default function HireAgreementTwoStepForm({
  toApiUrl,
  editMode = false,
  capchaKey,
  summaryMode = false,
  initialValues: propInitialValues,
}) {
  const [submitError, setSubmitError] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [isCheckingSubmission, setIsCheckingSubmission] = useState(true);
  const [submittedFormValues, setSubmittedFormValues] = useState(null);
  const captchaRef = useRef(null);
  const isReadOnly = summaryMode;
  const [locationAddress, setLocationAddress] = useState(null);

  const crud = React.useMemo(() => generateHireAgreementCrud(toApiUrl), [
    toApiUrl,
  ]);

  useEffect(() => {
    if (propInitialValues?.expires) {
      const expirationTime = parseInt(propInitialValues.expires, 10);
      const currentTime = Math.floor(Date.now() / 1000);
      setIsExpired(currentTime > expirationTime);
    }
    async function fetchAddress() {
      if (propInitialValues?.facility_name) {
        const addr = await fetchLocationAddressByName(propInitialValues.facility_name, crud);
        setLocationAddress(addr);
      }
    }
    fetchAddress();
  }, [propInitialValues?.facility_name, crud]);



  // Check if form has already been submitted
  useEffect(() => {
    const checkIfAlreadySubmitted = async () => {
      if (!propInitialValues?.i) {
        setIsCheckingSubmission(false);
        return;
      }

      try {
        const response = await crud.getSuccess({ id: propInitialValues.i });
        if (response.data.success) {
          setIsAlreadySubmitted(true);
        } else {
          setIsAlreadySubmitted(false);
        }
      } catch (error) {
        // If error is 404, form hasn't been submitted yet
        if (error.response?.status !== 404) {
          console.error('Error checking submission status:', error);
        }
      } finally {
        setIsCheckingSubmission(false);
      }
    };

    checkIfAlreadySubmitted();
  }, [propInitialValues?.i, toApiUrl]);

  const mergedInitialValues = React.useMemo(() => ({
    ...INITIAL_VALUES,
    ...propInitialValues,
  }), [propInitialValues]);

  const { submitForm: apiSubmitForm } = crud;

  // Single reusable PDF generation function
  const generatePDF = async (formValues = null) => {
    const doc = new jsPDF();
    
    // Use submitted form values if available, otherwise use propInitialValues
    const values = formValues || propInitialValues;
    
    // Set up fonts and styles
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Header
    doc.setFillColor(59, 130, 246); // Blue color
    doc.rect(0, 0, 220, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('HIRE AGREEMENT', 105, 20, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    
    let yPosition = 50;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
   
    doc.text(`Submission Date: ${new Date().toLocaleDateString()}`, 20, yPosition);
    yPosition += 15;
    
    // Organization Details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Organization Information', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const orgData = [
      ['Organization Name:', capitalizeWords(values?.organization || 'N/A')],
      ['ABN:', values?.abn || 'N/A'],
      ['Address:', capitalizeWords(values?.address || 'N/A')],
      ['Contact Person:', `${capitalizeWords(values?.contact_first_name || '')} ${capitalizeWords(values?.contact_last_name || '')}`.trim() || 'N/A'],
      ['Phone:', values?.phone || 'N/A'],
      ['Email:', (values?.email || 'N/A').toLowerCase()],
      ['Person Present at Booking:', `${capitalizeWords(values?.present_first_name || '')} ${capitalizeWords(values?.present_last_name || '')}`.trim() || 'N/A'],
    ];
    
    orgData.forEach(([label, value]) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(label, 20, yPosition);
      doc.text(value, 80, yPosition);
      yPosition += 8;
    });
    
    yPosition += 10;
    
    // Facility Details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Facility & Booking Details', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    

    let actualFacilityAddress = capitalizeWords(locationAddress || values?.location_address || 'N/A');
    if (!locationAddress || actualFacilityAddress === 'N/A') {
      if (values?.facility_name) {
        const fetchedAddress = await fetchLocationAddressByName(values.facility_name, crud);
        if (typeof fetchedAddress === 'string' && fetchedAddress) {
          actualFacilityAddress = capitalizeWords(fetchedAddress);
        }
      }
    }

    const facilityName = values?.facility_name || 'N/A';
    
    // Get area and sub-area names
    const selectedAreas = values?.area_names ? 
      (typeof values.area_names === 'string' ? values.area_names.split(',') : values.area_names) :
      [];

    const selectedSubAreas = values?.sub_area_names
      ? (typeof values.sub_area_names === 'string'
          ? values.sub_area_names.split(',').map(s => s.trim())
          : values.sub_area_names)
      : [];

    const facilityData = [
      ['Facility:', facilityName],
      ['Facility Address:', actualFacilityAddress],
      ['Areas:', Array.isArray(selectedAreas) ? selectedAreas.join(', ') : 'N/A'],
      ['Sub Areas:', Array.isArray(selectedSubAreas) ? selectedSubAreas.join(', ') : 'N/A'],
      ['Hiring Purpose:', capitalizeWords(values?.hiring_purpose || 'N/A')],
      ['Equipment Required:', capitalizeWords(values?.equipment_required || 'N/A')],
      ['Expected Spectators:', String(values?.spectators || 'N/A')],
      ['Total Attendance:', String(values?.total_attendance || 'N/A')],
      ['Start Date:', values?.start_date ? new Date(values.start_date).toLocaleDateString() : 'N/A'],
      ['End Date:', values?.end_date ? new Date(values.end_date).toLocaleDateString() : 'N/A'],
      ['In Time:', values?.in_time || 'N/A'],
      ['Out Time:', values?.out_time || 'N/A'],
      ['Hiring Days:', Array.isArray(values?.hiring_days) ? values.hiring_days.map(capitalizeWords).join(', ') : 'N/A'],
      ['Continues School Holidays:', values?.continues_school_holidays === '1' ? 'Yes' : 'No'],
      ['Has Booking Exceptions:', values?.has_booking_exceptions === '1' ? 'Yes' : 'No'],
      ['Charging Attendance:', values?.is_charging_attendance === '1' ? 'Yes' : 'No'],
    ];
    // Add booking exception dates if they exist
    if (values?.has_booking_exceptions === '1' && values?.booking_exception_dates) {
      // Handle different formats of booking_exception_dates
      let exceptionDates = values.booking_exception_dates;
      
      // If it's a string, try to parse it as JSON
      if (typeof exceptionDates === 'string') {
        try {
          exceptionDates = JSON.parse(exceptionDates);
        } catch (e) {
          console.warn('Failed to parse booking_exception_dates for PDF:', e);
          exceptionDates = [];
        }
      }
      
      // Ensure it's an array and has items
      if (Array.isArray(exceptionDates) && exceptionDates.length > 0) {
        const exceptionDatesText = exceptionDates.map(item => {
          // Handle both object format (with type, date, startDate, endDate) and string format
          if (typeof item === 'string') {
            return new Date(item).toLocaleDateString();
          } else if (item.type === 'single') {
            return new Date(item.date).toLocaleDateString();
          } else if (item.startDate && item.endDate) {
            return `${new Date(item.startDate).toLocaleDateString()} - ${new Date(item.endDate).toLocaleDateString()}`;
          } else {
            return new Date(item).toLocaleDateString();
          }
        }).join(', ');
        facilityData.push(['Exception Dates:', exceptionDatesText]);
      }
    }
    
    facilityData.forEach(([label, value]) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(label, 20, yPosition);
      doc.text(value, 80, yPosition);
      yPosition += 8;
    });
    
    yPosition += 10;
    
    // Payment Details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment & Invoice Details', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const paymentData = [
      [
        'Invoice Contact:',
        invoiceContactOptionDisplay[values?.invoice_contact_option] || capitalizeWords(values?.invoice_contact_option || 'N/A'),
      ],
      [
        'Payment Option:',
        paymentOptionDisplay[values?.payment_option] || capitalizeWords(values?.payment_option || 'N/A'),
      ],
    ];
    
    if (values?.invoice_contact_option === 'different_contact') {
      paymentData.push(['Invoice Contact Name:', `${values?.invoice_first_name || ''} ${values?.invoice_last_name || ''}`.trim() || 'N/A']);
      paymentData.push(['Invoice Email:', values?.invoice_email || 'N/A']);
    }
    
    paymentData.forEach(([label, value]) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(label, 20, yPosition);
      doc.text(value, 80, yPosition);
      yPosition += 8;
    });
    
    yPosition += 10;
    
    // Additional Notes
    if (values?.additional_notes) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Additional Notes', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const notes = doc.splitTextToSize(values.additional_notes, 170);
      notes.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
      
      yPosition += 10;
    }
    
    // Terms and Conditions - Use the exact terms from the form
    doc.addPage();
    yPosition = 20;
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('TERMS AND CONDITIONS', 105, yPosition, { align: 'center' });
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    // Use the exact terms and conditions from the form constants
    const actualTermsAndConditions = termsAndConditions;
    
    actualTermsAndConditions.forEach((term, termIdx) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${termIdx + 1}. ${term.title}`, 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      
      term.clauses.forEach((clause, clauseIdx) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Handle clauses with subClauses
        if (clause.subClauses && Array.isArray(clause.subClauses)) {
          // Main clause text
          const actualFacilityName = facilityName;
          let text = clause.text.replace(/"Facility Name"/g, actualFacilityName);
          text = text.replace(/"Facility Address"/g, actualFacilityAddress);
          const lines = doc.splitTextToSize(text, 170);
          
          lines.forEach((line, idx) => {
            if (yPosition > 250) {
              doc.addPage();
              yPosition = 20;
            }
            if (idx === 0) {
              doc.text(`${String.fromCharCode(97 + clauseIdx)}. ${line}`, 20, yPosition);
            } else {
              doc.text(`  ${line}`, 32, yPosition); // Indent continuation lines
            }
            yPosition += 6;
          });
          
          // Handle subClauses
          clause.subClauses.forEach((subClause) => {
            let subText = subClause.text.replace(/"Facility Name"/g, actualFacilityName);
            subText = subText.replace(/"Facility Address"/g, actualFacilityAddress);
            const subLines = doc.splitTextToSize(subText, 155); // slightly narrower for indent
            subLines.forEach(subLine => {
              if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
              }
              doc.text(`   - ${subLine}`, 32, yPosition); // Indented dash bullet
              yPosition += 6;
            });
          });
        } else {
          // Simple clause with just text
          const actualFacilityName = facilityName;
          let text = clause.text.replace(/"Facility Name"/g, actualFacilityName);
          text = text.replace(/"Facility Address"/g, actualFacilityAddress);
          const lines = doc.splitTextToSize(text, 170);
          
          lines.forEach((line, idx) => {
            if (yPosition > 250) {
              doc.addPage();
              yPosition = 20;
            }
            if (idx === 0) {
              doc.text(`${String.fromCharCode(97 + clauseIdx)}. ${line}`, 20, yPosition);
            } else {
              doc.text(`  ${line}`, 32, yPosition); // Indent continuation lines
            }
            yPosition += 6;
          });
        }
        
        yPosition += 3;
      });
      
      yPosition += 5;
    });
    
    // Signature Section
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE', 105, yPosition, { align: 'center' });
    yPosition += 15;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('I acknowledge that I have read, understood, and agree to the terms and conditions outlined in this hire agreement.', 20, yPosition);
    yPosition += 20;
    
    // Add signer name
    if (values?.signed_name) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Name: ${capitalizeWords(values.signed_name)}`, 20, yPosition);
      yPosition += 5;
    }
    
    // Add signature if available
    if (values?.signature) {
      try {
        // For now, just show the signature as text to avoid PNG corruption issues
        doc.addImage(values.signature, 'PNG', 20, yPosition, 120, 60);
        yPosition += 25;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        doc.addImage(values.signature, 'PNG', 20, yPosition, 120, 60);
        yPosition += 25;
      }
    } else {
      doc.text('Signature: [Digital signature provided]', 20, yPosition);
      yPosition += 25;
    }

    yPosition += 25;
    
    // Add signed date under the signature
    const signedDate = new Date().toISOString().split('T')[0];
    doc.text(`Signed Date: ${signedDate}`, 20, yPosition);
    yPosition += 10;
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 280);
    
    // Save the PDF
    const orgName = values?.organization || 'Organization';
    const facilityNameForFile = facilityName || 'Facility';
    const cleanOrgName = orgName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const cleanFacilityName = facilityNameForFile.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`Hire_Agreement_${cleanOrgName}_${cleanFacilityName}_${dateStr}.pdf`);
  };



  const handleFinalSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitError('');

    try {
      await apiSubmitForm({ 
        i: values.i,
        termsCheck: values.termsCheck,
        signature: values.signature,
        signed_name: values.signed_name,
       });
      setSubmittedFormValues(values);
      setIsSuccessfullySubmitted(true);
      return;
      
      
    } catch (apiError) {
      console.error('Submit error:', apiError);
      
      // Handle duplicate submission error
      if (apiError?.response?.status === 409) {
        setSubmitError('This form has already been submitted. If you need to make changes or have questions, please contact our support team.');
      } else if (apiError?.response?.status === 400) {
        // Check if it's an "already submitted" error
        const errorMessage = apiError?.response?.data?.message || '';
        if (errorMessage.toLowerCase().includes('already submitted') || 
            errorMessage.toLowerCase().includes('duplicate')) {
          setSubmitError('This form has already been submitted previously. Please contact support if you need to make changes.');
        } else {
          setSubmitError(errorMessage || 'Invalid form data. Please check your inputs and try again.');
        }
      } else {
        setSubmitError(
          apiError?.response?.data?.message ||
            'Something went wrong. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking submission status
  if (isCheckingSubmission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <CircularProgress size={60} sx={{ mb: 4 }} />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Checking Form Status
          </h2>
          <p className="text-gray-600">
            Please wait while we verify your form submission status...
          </p>
        </div>
      </div>
    );
  }

  // Show successfully submitted message (first-time submission)
  if (isSuccessfullySubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
       
          <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Successfully Submitted!
          </h1>

          
          <p className="text-lg text-gray-600 mb-6">
            Your hire agreement form has been successfully submitted and is being processed.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => generatePDF(submittedFormValues)}
              className="px-8 py-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Download Hire Agreement
            </button>

          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@bluefit.com.au" className="text-blue-600 hover:underline">
                support@bluefit.com.au
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show already submitted message
  if (isAlreadySubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
       
          <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Form Already Submitted
          </h1>

          
          <p className="text-lg text-gray-600 mb-6">
            This hire agreement form has already been submitted and is being processed.
          </p>

          {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => generatePDF(submittedFormValues)}
              className="px-8 py-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Download Hire Agreement
            </button>

          </div> */}

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@bluefit.com.au" className="text-blue-600 hover:underline">
                support@bluefit.com.au
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={mergedInitialValues}
      validationSchema={validationHireAgreementSchema}
      onSubmit={handleFinalSubmit}
      enableReinitialize
    >
      {(formik) => {
        const {
          values,
          isSubmitting,
          errors,
          touched,
          handleSubmit,
          isValid,
        } = formik;

        return (
          <Box className="flex flex-col min-h-screen">
            {/* Expiration message */}
            {isExpired && (
              <Box sx={{ mb: 2, width: '75%' }}>
                <Alert severity="warning">
                  <AlertTitle>Form Expired</AlertTitle>
                  This form is no longer valid as it has expired. Please contact the administrator for a new form.
                </Alert>
              </Box>
            )}

            {/* Global submit error */}
            {submitError && (
              <Box sx={{ mb: 2, width: '75%' }}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  {submitError}
                </Alert>
              </Box>
            )}

            <Form 
              className="flex-1 flex flex-col items-center w-full" 
              noValidate
              onSubmit={(e) => {
                if (isExpired) {
                  e.preventDefault();
                  return;
                }
                handleSubmit(e);
              }}
            >
              <Box className="w-11/12">
                <MainStep
                  capchaKey={capchaKey}
                  captchaRef={captchaRef}
                  isReadOnly={isReadOnly}
                  initialValues={propInitialValues}
                  crud={crud}
                  locationAddress={locationAddress}
                />
              </Box>

              <Divider className="w-3/4 my-8" />

              <Box className="w-3/4 flex justify-end">
                <Button
                  variant="contained"
                  type="submit"  
                  className="w-1/2"
                  disabled={
                    (() => {
                      const disabled = isSubmitting || 
                        isExpired || 
                        !isValid ||
                        !values.termsCheck ||
                        !values.signed_name ||
                        !isValidSignature(values.signature) ||
                        !values.recaptcha_token;
                      
                      return disabled;
                    })()
                  }
                  sx={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#2563eb',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#9ca3af !important',
                      color: '#6b7280 !important',
                      cursor: 'not-allowed !important',
                      '&:hover': {
                        backgroundColor: '#9ca3af !important',
                      },
                      '&:focus': {
                        backgroundColor: '#9ca3af !important',
                      },
                    },
                  }}
                >
                  {isSubmitting ? 'Submitting…' : 
                   isExpired ? 'Form Expired' :
                   !isValid ? 'Please Complete All Required Fields' :
                   !values.termsCheck ? 'Accept Terms & Conditions' :
                   !values.signed_name ? 'Enter Signer Name' :
                   !isValidSignature(values.signature) ? 'Add Signature' :
                   !values.recaptcha_token ? 'Complete Captcha Verification' :
                   'Submit'}
                </Button>
              </Box>
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
}

HireAgreementTwoStepForm.propTypes = {
  toApiUrl: PropTypes.func,
  editMode: PropTypes.bool,
  capchaKey: PropTypes.string,
  summaryMode: PropTypes.bool,
  initialValues: PropTypes.object,
};
